import { type ReactNode } from "react";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTDropdownButton } from "commonComponents/btWrappers/BTDropdownButton/BTDropdownButton";
import {
    type IBTItemHeaderSecondaryAction,
    type TBTItemHeaderIcon,
} from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTSplitButton } from "commonComponents/btWrappers/BTSplitButton/BTSplitButton";
import { shouldShowApproveAndDeclineActionsColumn } from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

import { Statuses } from "handlers";

export interface ISelectionOptionHeaderActionsProps {
    disabled: boolean;
    isParsingProductUrl: boolean;
    hasDeletePermission: boolean;
    hasUpdatePermission: boolean;
    isCreating: boolean;
    isEditing: boolean;
    isExistingEntity: boolean;
    isModal?: boolean;
    status?: number;
    onCancel: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onSave: () => void;
    onSaveAndClose: () => void;
    onSaveAndNew: () => void;
    onApprove?: () => void;
    onDecline?: () => void;
    onReset?: () => void;
    onSend?: () => void;
    onCopy: () => void;
    canCopy: boolean;
    canSaveToCatalog: boolean;
    onSaveToCatalog: () => void;
    isSavingToCatalog: boolean;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    openPurchaseOrderLinkingWizard: (toExisting: boolean) => void;
    /** When set, Send/Approve/Decline are disabled and show this message as a hover tooltip. */
    coBlockMessage?: string | null;
}

/**
 * The action configuration a selection-option header feeds into
 * {@link BTItemHeader.ActionButtons}: a single primary action, any number of
 * secondary actions (pinned or roll-up), and the mapped icon actions (Copy /
 * Delete) that live in the overflow menu.
 */
export interface ISelectionOptionHeaderActionsConfig {
    primaryAction: ReactNode;
    secondaryActions: (ReactNode | IBTItemHeaderSecondaryAction)[];
    iconActions: TBTItemHeaderIcon[];
}

const CO_BLOCK_TOOLTIP = "A change order needs to be approved first";

/**
 * Builds the selection-option header's action configuration (primary /
 * secondary / icon actions) from the current status, permissions, and portal.
 * Consumed by {@link SelectionOptionHeader} and spread into
 * {@link BTItemHeader.ActionButtons} so the actions follow the standardized
 * item-header toolbar pattern instead of a bespoke overflow bar.
 */
export function useSelectionOptionHeaderActions(
    props: Readonly<ISelectionOptionHeaderActionsProps>,
): ISelectionOptionHeaderActionsConfig {
    const {
        disabled,
        isParsingProductUrl,
        hasDeletePermission,
        hasUpdatePermission,
        isCreating,
        isEditing,
        isExistingEntity,
        isModal = false,
        status,
        canCreatePurchaseOrder = false,
        canAddToExistingPurchaseOrder = false,
        openPurchaseOrderLinkingWizard,
        coBlockMessage,
    } = props;
    const userInfo = useRequiredContext(UserInfoContext);
    const isBuilder = userInfo.loginType === BTLoginTypes.BUILDER;
    const isClient =
        userInfo.loginType === BTLoginTypes.OWNER || userInfo.loginType === BTLoginTypes.CONTACT;

    const shouldShowApproveDeclineActions =
        isExistingEntity &&
        shouldShowApproveAndDeclineActionsColumn(userInfo.loginType, hasUpdatePermission);

    // The reset button will not be present on the client portal.
    // Recall applies to Sent (PENDING) options too — transitions them back to Draft.
    const showReset =
        isBuilder &&
        shouldShowApproveDeclineActions &&
        (status === Statuses.APPROVED ||
            status === Statuses.DECLINED ||
            status === Statuses.PENDING);

    // Send is the primary action for builders on draft options — transitions Draft → Sent.
    const showSend = isBuilder && shouldShowApproveDeclineActions && status === Statuses.UNRELEASED;

    const isEditableStatus = status === Statuses.PENDING || status === Statuses.UNRELEASED;

    const showDecline = shouldShowApproveDeclineActions && isEditableStatus;
    const showApprove = shouldShowApproveDeclineActions && isEditableStatus;

    // The delete button will not be present on the client portal
    const showDelete =
        isBuilder &&
        hasDeletePermission &&
        isExistingEntity &&
        (status === Statuses.PENDING ||
            status === Statuses.DECLINED ||
            status === Statuses.UNRELEASED);

    const showCancel = isCreating || (isModal && !isEditing && isBuilder);
    const showSave =
        isCreating || ((isEditableStatus || status == null) && hasUpdatePermission && isEditing);

    const hasPrimaryApproveButton = isClient && status === Statuses.PENDING;

    // If the user is a builder and is viewing the selection option in a modal,
    // we will hide the secondary header buttons to prevent editing.
    const hideSecondaryHeaderButtons = isBuilder && isModal;

    const showAddToPurchaseOrder = canCreatePurchaseOrder || canAddToExistingPurchaseOrder;

    // When Send occupies the primary slot, Save demotes to a secondary-styled button;
    // otherwise Save is the primary action.
    const saveButton = showSave ? (
        <BTSplitButton
            data-testid="selection-option-primary-action-with-related-actions"
            type={showSend ? "secondary" : "primary"}
            disabled={disabled || isParsingProductUrl}
            buttonAction={{
                content: "Save",
                onClick: props.onSave,
                "data-testid": "save",
            }}
            menuItems={
                isModal
                    ? undefined
                    : [
                          {
                              "data-testid": "saveAndNew",
                              content: "Save and new",
                              onClick: props.onSaveAndNew,
                          },
                          {
                              "data-testid": "saveAndClose",
                              content: "Save and close",
                              onClick: props.onSaveAndClose,
                          },
                      ]
            }
        />
    ) : null;

    const sendButton = (
        <BTPopover content={coBlockMessage ? CO_BLOCK_TOOLTIP : undefined}>
            <span>
                <BTButton
                    type="primary"
                    data-testid="send"
                    disabled={disabled || !!coBlockMessage}
                    onClick={props.onSend}
                >
                    Send
                </BTButton>
            </span>
        </BTPopover>
    );

    const approveButton = (
        <BTPopover content={coBlockMessage ? CO_BLOCK_TOOLTIP : undefined}>
            <span>
                <BTButton
                    type={hasPrimaryApproveButton ? "primary" : "secondary"}
                    data-testid="approve"
                    aria-label="Approve"
                    disabled={disabled || !!coBlockMessage}
                    onClick={props.onApprove}
                >
                    Approve
                </BTButton>
            </span>
        </BTPopover>
    );

    const declineButton = (
        <BTPopover content={coBlockMessage ? CO_BLOCK_TOOLTIP : undefined}>
            <span>
                <BTButton
                    type="secondary"
                    data-testid="decline"
                    aria-label="Decline"
                    disabled={disabled || !!coBlockMessage}
                    onClick={props.onDecline}
                >
                    Decline
                </BTButton>
            </span>
        </BTPopover>
    );

    const cancelButton = (
        <BTButton
            type="secondary"
            data-testid="cancel"
            disabled={disabled || isParsingProductUrl}
            onClick={props.onCancel}
        >
            Cancel
        </BTButton>
    );

    const resetButton = (
        <BTButton type="secondary" data-testid="reset" disabled={disabled} onClick={props.onReset}>
            Recall
        </BTButton>
    );

    const addToButton = (
        <BTDropdownButton
            data-testid="addToDropdown"
            type="secondary"
            menuItems={[
                {
                    "data-testid": "newPurchaseOrder",
                    content: "New PO",
                    disabled: !canCreatePurchaseOrder,
                    onClick: () => openPurchaseOrderLinkingWizard(false),
                },
                {
                    "data-testid": "existingPurchaseOrder",
                    content: "Existing PO",
                    disabled: !canAddToExistingPurchaseOrder,
                    onClick: () => openPurchaseOrderLinkingWizard(true),
                },
            ]}
        >
            Add to
        </BTDropdownButton>
    );

    // Send takes the single primary slot when present; Save then demotes to a pinned
    // secondary action (keeping its dropdown variants). When Send is absent, Save is the
    // primary action. Approve/Decline are always pinned secondary actions (below); the
    // client's Approve keeps its primary *styling* via the button's own `type`, but must
    // not displace them from the slot — otherwise a client editing a PENDING option (Save
    // primary) would lose Approve.
    let primaryAction: ReactNode = null;
    if (showSend) {
        primaryAction = sendButton;
    } else if (showSave) {
        primaryAction = saveButton;
    }

    const secondaryActions: (ReactNode | IBTItemHeaderSecondaryAction)[] = [];
    const iconActions: TBTItemHeaderIcon[] = [];

    // Builders viewing in a modal only ever see the (suppressed) primary action —
    // every secondary/overflow action is hidden to prevent editing from the modal.
    if (!hideSecondaryHeaderButtons) {
        // Roll-up actions (unpinned): collapse into the overflow menu under width pressure.
        if (showCancel) {
            secondaryActions.push(cancelButton);
        }
        if (showReset) {
            secondaryActions.push(resetButton);
        }
        if (showAddToPurchaseOrder) {
            secondaryActions.push(addToButton);
        }

        if (showDecline) {
            secondaryActions.push(declineButton);
        }
        if (showApprove) {
            secondaryActions.push(approveButton);
        }

        // When Send is primary, Save moves alongside it as a pinned secondary action.
        if (showSend && showSave) {
            secondaryActions.push({ node: saveButton, pinned: true });
        }

        // Copy / Delete / Save to catalog always live in the overflow menu (per the
        // item-header design guide). Save to catalog stays disabled while a save is in
        // flight (re-entrancy guard).
        if (props.canCopy) {
            iconActions.push({ icon: "copy", onClick: props.onCopy, inMenu: true });
        }
        if (props.canSaveToCatalog) {
            iconActions.push({
                icon: "saveToCatalog",
                onClick: props.onSaveToCatalog,
                disabled: disabled || props.isSavingToCatalog,
                inMenu: true,
            });
        }
        if (showDelete) {
            iconActions.push({ icon: "delete", onClick: props.onDelete, inMenu: true });
        }
    }

    return { primaryAction, secondaryActions, iconActions };
}
